import React, { useEffect, useMemo, useState } from "react";
import { apiJson } from "../../../utils/api";
import en from "../../../i18n/en.json";
import hi from "../../../i18n/hi.json";

// Flatten a nested dict into [{ key: "common.save", value: "Save" }]
function flatten(obj, prefix = "") {
  const out = [];
  for (const k of Object.keys(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    const v = obj[k];
    if (v && typeof v === "object" && !Array.isArray(v)) out.push(...flatten(v, path));
    else out.push({ key: path, value: v });
  }
  return out;
}

const TranslationManagement = () => {
  const [overrides, setOverrides] = useState({}); // `${key}::${lang}` -> value
  const [drafts, setDrafts] = useState({}); // local edit buffer
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("");
  const [section, setSection] = useState("");

  // Build the merged row list from EN as the canonical key set
  const rows = useMemo(() => {
    const enFlat = flatten(en);
    const hiFlat = flatten(hi);
    const hiMap = Object.fromEntries(hiFlat.map((r) => [r.key, r.value]));
    return enFlat.map((r) => ({
      key: r.key,
      section: r.key.split(".")[0],
      enDefault: r.value,
      hiDefault: hiMap[r.key] || "",
    }));
  }, []);

  const sections = useMemo(() => Array.from(new Set(rows.map((r) => r.section))), [rows]);

  const load = () => {
    apiJson("/api/admin/master/translations")
      .then((items) => {
        const m = {};
        for (const i of items) m[`${i.key}::${i.lang}`] = i.value;
        setOverrides(m);
      })
      .catch((e) => setError(e.message));
  };
  useEffect(load, []);

  const draftValue = (key, lang) => {
    const dk = `${key}::${lang}`;
    if (dk in drafts) return drafts[dk];
    return overrides[dk] ?? "";
  };

  const setDraft = (key, lang, value) => {
    setDrafts({ ...drafts, [`${key}::${lang}`]: value });
  };

  const save = async (key, lang) => {
    const value = draftValue(key, lang);
    setSavingKey(`${key}::${lang}`);
    setError("");
    setMessage("");
    try {
      await apiJson("/api/admin/master/translations", {
        method: "PUT",
        body: JSON.stringify({ key, lang, value }),
      });
      // Update local state
      const next = { ...overrides };
      if (value) next[`${key}::${lang}`] = value;
      else delete next[`${key}::${lang}`];
      setOverrides(next);
      const nextDrafts = { ...drafts };
      delete nextDrafts[`${key}::${lang}`];
      setDrafts(nextDrafts);
      setMessage("Saved");
      setTimeout(() => setMessage(""), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingKey(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (section && r.section !== section) return false;
    if (filter) {
      const f = filter.toLowerCase();
      if (
        !r.key.toLowerCase().includes(f) &&
        !r.enDefault.toLowerCase().includes(f) &&
        !(r.hiDefault || "").includes(filter)
      )
        return false;
    }
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-xl font-bold text-gray-800 mb-2">Translations</h1>
      <p className="text-sm text-gray-600 mb-4">
        Override the bundled English and Hindi values. Empty an override to fall back to the default.
        Changes apply on next page load.
      </p>

      {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-3 text-sm">{error}</div>}
      {message && <div className="bg-green-50 text-green-700 p-2 rounded mb-3 text-sm">{message}</div>}

      <div className="bg-white p-3 rounded shadow mb-3 flex flex-wrap gap-3">
        <select value={section} onChange={(e) => setSection(e.target.value)} className="px-2 py-1 border rounded text-sm">
          <option value="">All sections</option>
          {sections.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          type="text"
          placeholder="Search key or text..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 min-w-[200px] px-2 py-1 border rounded text-sm"
        />
        <span className="text-xs text-gray-500 self-center">{filtered.length} of {rows.length} keys</span>
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
            <tr>
              <th className="text-left px-3 py-2 w-1/4">Key</th>
              <th className="text-left px-3 py-2 w-1/3">English</th>
              <th className="text-left px-3 py-2 w-1/3">हिंदी</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const enOverridden = `${r.key}::en` in overrides;
              const hiOverridden = `${r.key}::hi` in overrides;
              return (
                <tr key={r.key} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs text-gray-600 align-top">{r.key}</td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={draftValue(r.key, "en") || r.enDefault}
                      onChange={(e) => setDraft(r.key, "en", e.target.value)}
                      onBlur={() => save(r.key, "en")}
                      disabled={savingKey === `${r.key}::en`}
                      className={`w-full px-2 py-1 border rounded text-sm ${enOverridden ? "border-indigo-300 bg-indigo-50" : ""}`}
                    />
                    {enOverridden && (
                      <div className="text-[10px] text-gray-500 mt-0.5">Default: {r.enDefault}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <input
                      type="text"
                      value={draftValue(r.key, "hi") || r.hiDefault}
                      onChange={(e) => setDraft(r.key, "hi", e.target.value)}
                      onBlur={() => save(r.key, "hi")}
                      disabled={savingKey === `${r.key}::hi`}
                      className={`w-full px-2 py-1 border rounded text-sm ${hiOverridden ? "border-indigo-300 bg-indigo-50" : ""}`}
                    />
                    {hiOverridden && (
                      <div className="text-[10px] text-gray-500 mt-0.5">Default: {r.hiDefault}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TranslationManagement;
